import { useCallback } from 'react';
import { showAppNotification } from '@rainbow-modules/app';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { Todo } from 'data/firestore/agent/todo/types';
import { ItemUser } from 'data/firestore/globals';

interface CreateTodoPayload {
    type: 'opportunity';
    title: string;
    description?: string;
    status?: 'pending' | 'in_progress' | 'cancelled' | 'done';
    priority?: 'low' | 'medium' | 'high';
    opportunityId: string;
    pipelineId: string;
    stageId: string;
    assignedTo?: ItemUser;
    dueDate?: string;
    parentTodoId?: string;
    metadata?: Record<string, unknown>;
}

interface UpdateTodoPayload {
    title?: string;
    description?: string;
    status?: 'pending' | 'in_progress' | 'cancelled' | 'done';
    priority?: 'low' | 'medium' | 'high';
    assignedTo?: ItemUser | null;
    dueDate?: string | null;
    metadata?: Record<string, unknown>;
}

const useManageTodos = ({
    agentId,
    onTodoCreated = () => {},
    onTodoUpdated = () => {},
    onTodoDeleted = () => {},
} : {
    agentId: string;
    onTodoCreated?: (todo: Todo) => void;
    onTodoUpdated?: (todo: Todo) => void;
    onTodoDeleted?: (todoId: string) => void;
}) => {
    const { mutateAsync: createTodoMutation } = useHttpMutation<CreateTodoPayload, Todo>({
        method: 'POST',
        pathname: `/v1/agents/${agentId}/todos`,
    });

    const { mutateAsync: updateTodoMutation } = useHttpMutation<UpdateTodoPayload, Todo>({
        method: 'PATCH',
    });

    const { mutateAsync: deleteTodoMutation } = useHttpMutation({
        method: 'DELETE',
    });

    const createTodo = useCallback(
        async (payload: CreateTodoPayload) => {
            try {
                const todo = await createTodoMutation({
                    body: payload,
                });
                onTodoCreated(todo);
                showAppNotification({
                    title: 'Success',
                    description: 'TODO created successfully',
                    icon: 'success',
                    timeout: 3000,
                });
                return todo;
            } catch (error) {
                const message = (error as any)?.message || 'Failed to create TODO. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
                throw error;
            }
        },
        [createTodoMutation, onTodoCreated],
    );

    const updateTodo = useCallback(
        async (todoId: string, payload: UpdateTodoPayload) => {
            try {
                const todo = await updateTodoMutation({
                    pathname: `/v1/agents/${agentId}/todos/${todoId}`,
                    body: payload,
                });
                onTodoUpdated(todo);
                return todo;
            } catch (error) {
                const message = (error as any)?.message || 'Failed to update TODO. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
                throw error;
            }
        },
        [agentId, updateTodoMutation, onTodoUpdated],
    );

    const cancelTodo = useCallback(
        async (todoId: string) => {
            try {
                await deleteTodoMutation({
                    pathname: `/v1/agents/${agentId}/todos/${todoId}`,
                });
                onTodoDeleted(todoId);
                showAppNotification({
                    title: 'Success',
                    description: 'TODO cancelled successfully',
                    icon: 'success',
                    timeout: 3000,
                });
            } catch (error) {
                const message = (error as any)?.message || 'Failed to cancel TODO. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
                throw error;
            }
        },
        [agentId, deleteTodoMutation, onTodoDeleted],
    );

    return {
        createTodo,
        updateTodo,
        cancelTodo,
    };
};

export default useManageTodos;
